// app/analytics/SectionView.tsx

/**
 * ---------------------------------------------------------------------------
 * <SectionView /> - GA4 section-in-view instrumentation
 * ---------------------------------------------------------------------------
 * Wraps a page section and fires a single `section_view` event the first time
 * that section is scrolled into meaningful view. Unlike `<ScrollDepth />`,
 * which reports raw page-height milestones (25/50/75/90 %), this reports which
 * *named* section a visitor actually reached - so a funnel like
 * hero -> paper_tests -> how_it_works -> join_a_slot can be read directly,
 * independent of how tall each section happens to be.
 *
 * Event: `section_view` with params:
 *   - `section`  (string) stable snake_case id, e.g. "join_a_slot"
 *   - `position` (int)    1-based order of the section within the page
 * Page and journey context are attached by `trackJourneyEvent`, so the same
 * consented event reaches GA4 and the first-party journey stream.
 *
 * Implementation notes
 * • Fires at most once per mount; the observer disconnects after firing.
 * • "In view" = the section's top edge has crossed into the upper 70 % of the
 *   viewport (rootMargin bottom -30 %), so a section merely peeking at the very
 *   bottom of the screen does not count as seen.
 * • No-ops when IntersectionObserver is unavailable (very old browsers / SSR).
 * • The wrapper `<div>` carries no padding, margin, or border, so it does not
 *   disturb the full-bleed negative-margin bands it wraps.
 * ---------------------------------------------------------------------------
 */

"use client";

import { useEffect, useRef, type ReactNode } from "react";
import { trackJourneyEvent } from "@/lib/journey-analytics";

export default function SectionView({
  section,
  position,
  children,
}: {
  section: string;
  position: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            trackJourneyEvent("section_view", { section, position });
            observer.disconnect();
          }
        }
      },
      // Fire once the section top reaches the upper 70 % of the viewport.
      { threshold: 0, rootMargin: "0px 0px -30% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [section, position]);

  return (
    <div ref={ref} data-section={section}>
      {children}
    </div>
  );
}
